import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import { omit, truncate } from 'lodash'
import { Column } from 'react-table'
import { Trans, t } from '@lingui/macro'

import {
  AnalyticsSurveyUsersFilter,
  SqlOrderDirectionEnum,
  SurveyUsersOverviewOrderColumn
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'

import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { linkBuilder } from 'shared/linkBuilder'
import { getPagesCount } from '../utils'
import Tag from '../../../components/common/tag/Tag'
import { useSurveyUsersAnalyticsOverviewQuery } from './SurveysOverviewTable.codegen'
import { AnalyticsContext } from '../AnalyticsContext'

interface IProps {
  userId: number
}

export const SurveysOverviewTable: FC<IProps> = ({ userId }: IProps) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewFilter, setOverviewFilters] =
    useState<AnalyticsSurveyUsersFilter>({
      limit: 20,
      orderColumn: SurveyUsersOverviewOrderColumn.createdAt,
      orderDirection: SqlOrderDirectionEnum.ASC
    })

  const overviewTableQuery = {
    ...overviewFilter,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useSurveyUsersAnalyticsOverviewQuery({
    variables: {
      mainAnalyticsFilter: omit(filterAsQueryVariables, 'groupIds'),
      overviewFilter: overviewTableQuery,
      userId
    }
  })

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Survey name`,
      id: SurveyUsersOverviewOrderColumn.surveyName,
      accessor: 'survey.title',
      Cell: (row) => {
        return (
          <>
            <strong className="dark">
              {truncate(row.value, { length: 80 })}
            </strong>
          </>
        )
      }
    },
    {
      Header: t`Sent`,
      accessor: 'created_at',
      id: SurveyUsersOverviewOrderColumn.createdAt,
      Cell: (row) => formatDateShort(row.value)
    },
    {
      Header: t`Opened`,
      accessor: 'started_at',
      id: SurveyUsersOverviewOrderColumn.startedAt,
      Cell: (row) => formatDateShort(row.value)
    },
    {
      Header: t`Responded`,
      accessor: 'completed_at',
      id: SurveyUsersOverviewOrderColumn.completedAt,
      Cell: (row) => formatDateShort(row.value)
    },
    {
      Header: t`Status`,
      id: SurveyUsersOverviewOrderColumn.status,
      Cell: (row) => {
        if (row.original.completed_at) {
          return (
            <Tag color="green">
              <Trans>Completed</Trans>
            </Tag>
          )
        } else if (row.original.started_at) {
          return (
            <Tag color="blue">
              <Trans>Opened</Trans>
            </Tag>
          )
        } else {
          return (
            <Tag color="clear">
              <Trans>Sent</Trans>
            </Tag>
          )
        }
      }
    }
  ]

  let pagesCount = 1
  if (data && data.user) {
    pagesCount = getPagesCount(
      data.user?.analytics?.surveys?.surveyUsersOverviewCount as number,
      overviewTableQuery.limit || 20
    )
  }

  return (
    <>
      <TableExtendedOld
        loading={loading}
        manual
        data={data?.user?.analytics?.surveys?.surveyUsersOverview}
        tableHeader={t`Surveys`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        // @ts-expect-error 9/11/2020
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={1}
        pages={pagesCount}
        filterFields={[SurveyUsersOverviewOrderColumn.surveyName]}
        getTrProps={(_state, row) => ({
          onClick: () =>
            navigateToAndResetPageAndSearch(
              linkBuilder.surveyDetailManage(row.original.survey.id),
              changeFilter
            ),
          className: 'clickable',
          ...DEFAULT_TR_PROPS
        })}
        defaultSortDesc
        defaultSorted={[
          {
            // @ts-expect-error 9/11/2020
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as SurveyUsersOverviewOrderColumn)
                : SurveyUsersOverviewOrderColumn.surveyName,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
    </>
  )
}
